import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/admin/audit")({
  component: AuditPage,
});

function AuditPage() {
  const [action, setAction] = useState("");
  const log = useQuery({
    queryKey: ["admin", "audit-log", action],
    queryFn: () => api.admin.auditLog({ action: action || undefined, limit: 200 }),
    staleTime: 10_000,
  });

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Audit log</h1>
        <p className="text-sm text-ink-500 mt-1">Historial de acciones administrativas y mutaciones críticas.</p>
      </header>

      <Card className="mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input
            className="pl-10"
            placeholder="Filtrar por acción (ej. admin.impersonate, admin.reset_password)…"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Eventos</CardTitle>
          <CardDescription>{log.data?.length ?? 0} entradas (últimas 200)</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
          {log.isLoading ? (
            Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)
          ) : !log.data || log.data.length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">Sin eventos.</p>
          ) : (
            log.data.map((e) => (
              <div key={e.id} className="py-3 grid grid-cols-[180px_1fr_120px] gap-3 items-center">
                <span className="text-[11px] text-ink-500 font-mono">
                  {new Date(e.at).toLocaleString("es-PE")}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={e.action.startsWith("admin.") ? "warn" : "muted"}>{e.action}</Badge>
                    {e.resource_type && (
                      <span className="text-xs text-ink-500">
                        {e.resource_type}#{e.resource_id ?? "—"}
                      </span>
                    )}
                  </div>
                  {Object.keys(e.payload).length > 0 && (
                    <pre className="text-[10px] font-mono text-ink-500 mt-1 truncate">
                      {JSON.stringify(e.payload)}
                    </pre>
                  )}
                </div>
                <span className="text-[11px] text-ink-500 font-mono">
                  user#{e.user_id ?? "—"} · {e.ip ?? "—"}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
